import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";
import Logo from "@/components/Logo";

const links = [
  { label: "Réalisations", href: "#realisations" },
  { label: "Avis", href: "#avis" },
  { label: "Services", href: "#services" },
  { label: "Zone d'intervention", href: "#zone" },
];

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-[background-color,border-color] duration-500 border-b ${
        scrolled || open
          ? "bg-black/60 backdrop-blur-xl border-white/10"
          : "bg-black/20 backdrop-blur-md border-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10 h-20 flex items-center justify-between">
        <a href="#accueil" data-testid="header-logo" className="flex items-center gap-3 leading-none">
          <Logo size={40} />
          <span>
            <span className="font-brand text-lg uppercase whitespace-nowrap">
              <span className="text-[10px] tracking-[0.35em] text-zinc-400 font-body font-bold mr-1.5 align-middle">Ent.</span>
              Goujon Vallée
            </span>
            <span className="flex gap-1 mt-1.5">
              <span className="h-1 w-7 bg-jaune" />
              <span className="h-1 w-7 bg-vert" />
            </span>
          </span>
        </a>
        <nav className="hidden xl:flex items-center gap-8" data-testid="header-nav">
          {links.map((l) => (
            <a
              key={l.href}
              href={l.href}
              data-testid={`nav-link-${l.href.slice(1)}`}
              className="text-[11px] tracking-[0.18em] uppercase text-zinc-400 hover:text-white transition-colors duration-300 whitespace-nowrap"
            >
              {l.label}
            </a>
          ))}
          <a
            href="#contact"
            data-testid="nav-cta-devis"
            className="group flex items-center gap-2 bg-white text-black px-5 py-2.5 text-[11px] tracking-[0.15em] uppercase font-bold hover:bg-jaune transition-colors duration-300 whitespace-nowrap"
          >
            Demander un devis
            <ArrowUpRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </a>
        </nav>
        <button
          data-testid="menu-toggle"
          onClick={() => setOpen(!open)}
          className="xl:hidden text-white p-2"
          aria-label="Menu"
        >
          {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>
      <AnimatePresence>
        {open && (
          <motion.nav
            data-testid="mobile-menu"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.35, ease: [0.33, 1, 0.68, 1] }}
            className="xl:hidden overflow-hidden bg-black/80 backdrop-blur-xl border-t border-white/10"
          >
            <div className="px-6 py-8 flex flex-col gap-6">
              {links.map((l) => (
                <a
                  key={l.href}
                  href={l.href}
                  data-testid={`mobile-nav-link-${l.href.slice(1)}`}
                  onClick={() => setOpen(false)}
                  className="text-sm tracking-[0.2em] uppercase text-zinc-300"
                >
                  {l.label}
                </a>
              ))}
              <a
                href="#contact"
                data-testid="mobile-nav-cta-devis"
                onClick={() => setOpen(false)}
                className="bg-white text-black px-5 py-3 text-xs tracking-[0.15em] uppercase font-bold text-center"
              >
                Demander un devis
              </a>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
}
